import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import SingleDatePicker from "components/input-range-data";
import { useCavOfflineSyncState } from "../ducks/hooks/state";
import { useCavOfflineSyncFilterActions } from "../ducks/hooks/action";
import { TransactionStatus } from "@/services/cav-offline-sync/types";
import { X } from "lucide-react";

const statusOptions: { value: TransactionStatus | "all"; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "pending", label: "Pendente" },
  { value: "failed", label: "Com Erro" },
  { value: "successful", label: "Processado" },
];

export function FilterBar() {
  const { filters } = useCavOfflineSyncState();
  const { updateFilter, clearFilters } = useCavOfflineSyncFilterActions();

  const dateFromValue = filters.dateFrom ? new Date(filters.dateFrom) : undefined;
  const dateToValue = filters.dateTo ? new Date(filters.dateTo) : undefined;

  const handleDateFromChange = (date: Date | undefined) => {
    const formattedDate = date ? format(date, "yyyy-MM-dd") : undefined;
    updateFilter("dateFrom", formattedDate);

    if (date && dateToValue && date > dateToValue) {
      updateFilter("dateTo", undefined);
    }
  };

  const handleDateToChange = (date: Date | undefined) => {
    const formattedDate = date ? format(date, "yyyy-MM-dd") : undefined;
    updateFilter("dateTo", formattedDate);

    if (date && dateFromValue && date < dateFromValue) {
      updateFilter("dateFrom", undefined);
    }
  };

  const handleStatusChange = (value: string) => {
    if (value === "all") {
      updateFilter("status", []);
    } else {
      updateFilter("status", [value as TransactionStatus]);
    }
  };

  const currentStatus =
    filters.status.length === 1 ? filters.status[0] : "all";

  const hasActiveFilters =
    filters.dateFrom ||
    filters.dateTo ||
    filters.status.length > 0 ||
    filters.storeIds.length > 0;

  return (
    <div className="flex flex-wrap gap-4 items-end p-4 bg-gray-50 rounded-lg mb-4">
      <div className="flex-1 min-w-[150px] max-w-[200px]">
        <SingleDatePicker
          id="dateFrom"
          date={dateFromValue}
          onDateChange={handleDateFromChange}
          label="Data - Ínicio"
          maxDate={dateToValue}
        />
      </div>

      <div className="flex-1 min-w-[150px] max-w-[200px]">
        <SingleDatePicker
          id="dateTo"
          date={dateToValue}
          onDateChange={handleDateToChange}
          label="Data - Fim"
          minDate={dateFromValue}
        />
      </div>

      <div className="flex-1 min-w-[150px] max-w-[200px]">
        <div className="flex flex-col gap-2">
          <Label className="block text-sm font-medium text-gray-700">
            Status
          </Label>
          <Select value={currentStatus} onValueChange={handleStatusChange}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Selecione o status" />
            </SelectTrigger>
            <SelectContent>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {hasActiveFilters && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="h-8 gap-2"
        >
          <X className="h-4 w-4" />
          Limpar Filtros
        </Button>
      )}
    </div>
  );
}
